/**
 * Lobby (§15.2 #2): the player list, the room code and a large join QR, the rules badge, the
 * proposed setup and the timers. The host chooses a preset or "random within budget", edits
 * the timers and the ⚙ advanced settings, connects big screens, and starts.
 */
import type { PresetId } from "@handshake/config";
import { PlayerChip } from "../../components/PlayerChip.tsx";
import { QrCode } from "../../components/QrCode.tsx";
import { RulesBadge } from "../../components/RulesBadge.tsx";
import { IntroLink } from "../../components/IntroLink.tsx";
import { RulesLink } from "../../components/RulesLink.tsx";
import { PRESET_LIST, presetName, presetOf, roleName } from "../../lib/labels.ts";
import { send, useGame } from "../../lib/store.ts";
import { useView } from "../../lib/view.ts";
import { ConnectScreenButton } from "../host/ConnectScreen.tsx";
import { AnalyticsConsent } from "./Analytics.tsx";
import { AdvancedSettingsEntry, LobbyTimersPanel } from "./slots.tsx";

export function joinUrl(publicUrl: string | null, code: string): string {
  const base = (publicUrl ?? window.location.origin).replace(/\/+$/, "");
  return `${base}/join/${code}`;
}

function countRoles(roles: readonly string[]): [string, number][] {
  const m = new Map<string, number>();
  for (const r of roles) m.set(r, (m.get(r) ?? 0) + 1);
  return [...m.entries()];
}

export function Lobby() {
  const view = useView();
  const welcome = useGame((s) => s.welcome);
  const session = useGame((s) => s.session);
  const pub = view.public;
  const code = session?.code ?? "";
  const isHost = view.me.isHost;
  const config = pub.config;
  const lobby = pub.lobby;
  const seated = pub.roster.length;
  const url = joinUrl(welcome?.publicUrl ?? null, code);

  const choose = (value: string) => {
    if (value === "random") {
      void send({ type: "configure", presetId: null, playerCount: null, overrides: {} });
    } else {
      void send({ type: "configure", presetId: value as PresetId });
    }
  };

  return (
    <div className="stack" data-testid="lobby">
      <section className="panel gold center">
        <div className="kicker">Room code</div>
        <div className="room-code" data-testid="room-code">
          {code}
        </div>
        <QrCode text={url} label={`Join QR code for room ${code}`} />
        <p className="faint">Scan to join, or open {url.replace(/^https?:\/\//, "")}</p>
        <RulesLink />
        <p style={{ margin: "0.5rem 0 0" }}>
          <IntroLink />
        </p>
      </section>

      <section className="panel">
        <div className="row">
          <h3 className="grow" style={{ margin: 0 }}>
            Guests ({seated})
          </h3>
          <RulesBadge rules={pub.rules} />
        </div>
        <ul className="list" data-testid="roster">
          {pub.roster.map((p) => (
            <li key={p.id}>
              <PlayerChip player={p} />
              <span className="grow" />
              {p.id === view.me.id ? <span className="tag plain">you</span> : null}
              {p.isHost ? <span className="tag">host</span> : null}
              {isHost && p.id !== view.me.id ? (
                <button
                  type="button"
                  className="btn small ghost"
                  aria-label={`Remove ${p.name}`}
                  onClick={() => void send({ type: "removePlayer", player: p.id })}
                >
                  ✕
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      </section>

      <section className="panel stack">
        <h3 style={{ margin: 0 }}>The setup</h3>
        {isHost ? (
          <label className="field">
            <span>Preset</span>
            <select
              className="text"
              data-testid="preset-select"
              value={config?.presetId ?? "random"}
              onChange={(e) => choose(e.target.value)}
            >
              <option value="random">Random within budget · follows the guests</option>
              {PRESET_LIST.map((id) => (
                <option key={id} value={id}>
                  {presetOf(id).name} · {presetOf(id).playerCount} players
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <SetupSummary />
        {lobby && lobby.errors.length > 0 ? (
          <ul className="list" aria-label="Setup problems">
            {lobby.errors.map((e, i) => (
              <li key={i} className="error-text">
                {e.message}
              </li>
            ))}
          </ul>
        ) : null}
        {lobby && lobby.warnings.length > 0 ? (
          <ul className="list" aria-label="Warnings">
            {lobby.warnings.map((w, i) => (
              <li key={i} className="muted">
                {w.message}
              </li>
            ))}
          </ul>
        ) : null}
        <AdvancedSettingsEntry />
      </section>

      <LobbyTimersPanel
        timers={pub.timers}
        isHost={isHost}
        players={config?.playerCount ?? Math.min(20, Math.max(6, seated))}
      />

      <AnalyticsConsent isHost={isHost} />

      {isHost ? <ConnectScreenButton /> : null}

      {isHost ? (
        <button
          type="button"
          className="btn primary big block"
          data-testid="start-game"
          disabled={!lobby?.canStart}
          onClick={() => void send({ type: "startGame" })}
        >
          Start the gala
        </button>
      ) : (
        <p className="center muted">Waiting for the host to start the game…</p>
      )}
      {isHost && !lobby?.canStart && seated < 6 ? (
        <p className="center faint">At least 6 guests are needed.</p>
      ) : isHost && lobby && lobby.errors.length > 0 ? (
        <p className="center error-text" data-testid="start-blocked">
          Fix the setup problems above to start.
        </p>
      ) : null}
    </div>
  );
}

function SetupSummary() {
  const view = useView();
  const config = view.public.config;
  if (!config) {
    return <p className="muted">The setup will follow once enough guests have joined.</p>;
  }
  const preset = presetName(config.presetId);
  const roles = config.teams.roles;
  return (
    <div className="stack" data-testid="setup-summary">
      <div>
        <strong>{preset ?? "Random within budget"}</strong>
        <div className="muted">
          {config.playerCount} players · {config.teams.agents} Agents · {config.clock.rounds} rounds
          · Leak limit {config.clock.leakLimit} · {config.cards.poisonVials}{" "}
          {config.cards.poisonVials === 1 ? "vial" : "vials"}
        </div>
      </div>
      {roles ? (
        <div className="row wrap">
          {countRoles(roles).map(([role, n]) => (
            <span key={role} className="tag plain">
              {roleName(role)}
              {n > 1 ? ` ×${n}` : ""}
            </span>
          ))}
        </div>
      ) : (
        <p className="faint">
          The app draws the roles at the start, within the power budget. The Guest List shows them
          at Arrival.
        </p>
      )}
    </div>
  );
}
